import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { schriftgroesseAnwenden } from './lib/darstellung'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { App } from './App'
import { ApiError } from './api/client'
import { huelleAnmelden } from './lib/huelle'
import { tastaturBeobachten } from './lib/tastatur'
import { beobachteTippziel } from './lib/tippziel'
import { vorratLaden, vorratMitschreiben } from './lib/vorrat'
import './styles/app.css'

// Muss vor dem ersten Zeichnen stehen: die Höhe der Anwendung hängt daran.
tastaturBeobachten()

// **Und das hier ebenfalls vor dem ersten Zeichnen.** Beim Kaltstart aus einer
// Benachrichtigung heraus kommt das Ziel, lange bevor React steht; wer erst
// später zuhört, hat es verpasst und landet auf der Startseite.
beobachteTippziel()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Eine fehlende oder abgelaufene Anmeldung lässt sich durch Wiederholen
      // nicht beheben -- dann wäre jeder Ladevorgang dreimal so langsam.
      retry: (failureCount, error) =>
        !(error instanceof ApiError && error.status < 500) && failureCount < 2,
      refetchOnWindowFocus: true,
      staleTime: 10_000,
      // Ohne Netz **scheitern**, nicht anhalten.
      //
      // Von Haus aus hält TanStack Query jede Anfrage an, solange der Browser
      // offline meldet: das Versprechen wird dann nie eingelöst, weder mit
      // Erfolg noch mit Fehler. Das hat hier einen handfesten Schaden
      // angerichtet -- die Vorbereitung eines Fotos wartete auf die
      // Einstellungen, die Anfrage hing, und die Kachel stand für immer auf
      // „…". Das Offline-Verhalten steckt in dieser Anwendung im
      // Ausgangskorb; dafür muss ein Fehlschlag ein Fehlschlag sein.
      networkMode: 'always',
    },
    mutations: {
      networkMode: 'always',
    },
  },
})

const container = document.getElementById('root')
if (!container) throw new Error('Kein Wurzelelement gefunden.')

// **Vor dem ersten Zeichnen.** Danach gesetzt, sähe man den Chat einmal in
// der Vorgabegröße aufblitzen und dann umspringen.
schriftgroesseAnwenden()

// **Der Service Worker wird jetzt von jedem Gerät angemeldet**, nicht mehr nur
// von denen mit Benachrichtigungen. Ohne ihn gibt es keine Hülle im
// Zwischenspeicher, und ohne die geht die App im Funkloch gar nicht erst auf.
// Ein Fehlschlag kostet genau das und sonst nichts -- deshalb geschluckt. Läuft
// nebenher, das Zeichnen wartet nicht darauf.
void huelleAnmelden().catch(() => undefined)

// **Der Vorrat kommt vor dem ersten Zeichnen, und darauf wird gewartet.**
// Käme er später, hätte `RequireAuth` längst zur Anmeldung weitergeleitet --
// ohne Netz scheitert `useMe()` ja sofort. Der gerettete Stand träfe dann auf
// eine Seite, die es nicht mehr gibt.
//
// Angehängt und nicht mit `await` davor: ein `await` auf oberster Ebene lehnt
// das Zielumfeld ab (Safari 14 steht in der Liste), und ein Bündel, das dort
// nicht lädt, wäre ein hoher Preis für eine schönere Zeile.
//
// `vorratLaden` bringt seine eigene Zeitschranke mit und schluckt jeden
// Fehlschlag. Der `catch` hier ist die zweite Sperre: **nichts** darf das
// Zeichnen verhindern -- eine Anwendung, die wegen ihres Zwischenspeichers
// schwarz bleibt, wäre schlimmer als eine ohne.
void vorratLaden(queryClient)
  .catch(() => false)
  .then(() => {
    vorratMitschreiben(queryClient)

    createRoot(container).render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </StrictMode>,
    )
  })
